import { Box, Button } from "@mui/material";
import { SubmissionStepStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback } from "react";

/**
 * Shared footer for stepper step editors that have nothing to persist (e.g.
 * Contacts, whose edits save through their own dialogs). Records the given step
 * complete and navigates to the next step in order. The button defaults to
 * "Save" to match `StepSaveFooter`; steps that only acknowledge and advance
 * (e.g. the read-only Understand step) pass `label="Continue"` instead.
 *
 * `backStep` (optional) adds a "Back" button that navigates to that step
 * without touching any step status — used by the document-form flow, whose
 * steps are walked linearly rather than from an overview.
 *
 * Renders nothing when the user cannot edit (read-only customers) or when the
 * submission is frozen (read-only after the snapshot is taken): there is no
 * continue action in either case.
 */
export function StepContinueFooter({
  submissionId,
  step,
  backStep,
  label = "Save",
}: {
  submissionId: string;
  step: SubmissionStepType;
  backStep?: SubmissionStepType;
  label?: string;
}) {
  const { isFrozen, canEdit } = useWasteStepper();
  const { touchStep, loading } = useTouchSubmissionMutation(submissionId);
  const { goToStep, goToNextStep } = useStepperNavigation();

  const handleContinue = useCallback(async () => {
    await touchStep(step, SubmissionStepStatus.Completed);
    goToNextStep(step);
  }, [touchStep, step, goToNextStep]);

  if (isFrozen || !canEdit) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
      {backStep && (
        <Button variant="text" disabled={loading} onClick={() => goToStep(backStep)}>
          Back
        </Button>
      )}
      <Button variant="contained" loading={loading} onClick={handleContinue}>
        {label}
      </Button>
    </Box>
  );
}
